/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {AuthInstance} from '@exc/auth';
import Core from './Core';
import CoreContext from './context';
import * as coreLocaleData from './__localization__/CoreStrings';
import {createBrowserHistory} from 'history';
import {ErrorTypes} from './enums';
import {getConfiguration} from './utils/config';
import {
  getCurrentTenantAndPath,
  getPathWithoutTenant,
  getQueryParams,
  getQueryValue,
  hashToPath
} from '@exc/url';
import HeroIcons from 'url:@exc/brand-icons/HeroIcons.svg';
import {history} from '@exc/router';
import I18nProvider, {I18nConsumer} from '@adobe/adobe-intl-react';
import {Internal} from '@adobe/exc-app/internal';
import metrics, {Level, Events as MetricsEvents} from '@adobe/exc-app/metrics';
import {preload} from '@exc/dom';
import Provider from '@react/react-spectrum/Provider';
import React from 'react';
import Sandbox from './modules/Sandbox';
import ServiceWorkerControllerService from './services/ServiceWorkerControllerService';
import ShellWait from '@exc/shell/src/ShellWait';
import unifiedShellSession from './services/UnifiedShellSessionService';

// Workaround to prevent code splitting - This CSS only has 4 styles.
import './modules/Error.css';

const SSO_PREFIX = '#/sso:';
const SSO_REGEX = /#\/sso:(.*?)(\/.*|$)/;

const ErrorView = React.lazy(() => import('./modules/Error'));

export {CoreContext, ErrorTypes, ErrorView, getPathWithoutTenant, getQueryValue, Sandbox, ServiceWorkerControllerService};

const waitComponent = (
  <div className="full-screen">
    <ShellWait locale="en-US" />
  </div>
);

export default class Main extends React.Component {
  constructor(props) {
    super(props);

    this.metrics = metrics.create('exc.core.App');
    this.shellTimer = this.metrics.start('Shell');

    let imsEnv = getQueryValue('ims');
    this.config = getConfiguration(props, imsEnv);
    let mode = getQueryValue('metricsmode') || (this.config.environment === 'prod' && 'quiet');

    mode && Internal.configureMetrics({mode});
    this.metrics.event(MetricsEvents.PAGE_LOAD_START, 'App constructed');

    if (window._srp && window._srp.resourceURL && window._srp.resourceURL !== this.config.cdn) {
      this.metrics.event('Shell.CDNFallback', `Using ${window._srp.resourceURL} for static assets.`, {level: Level.WARN});
      this.config.cdn = window._srp.resourceURL;
    }

    this.preloader = preload(document, this.config);
    this.preloader.onNeed('start');
    this.preloader.asset(HeroIcons, 'image');

    this.state = {
      auth: null,
      defaultComponent: waitComponent,
      error: null,
      firstRender: true,
      loading: true
    };

    // Auth history must be browser history in order to work due to how the URLs
    // and params are handled. `props.authHistory` is only used for testing.
    this.authHistory = props.authHistory || createBrowserHistory();
  }

  async componentDidMount() {
    this.setState({firstRender: false});
    const listeners = {
      'auth.accesstokenexpired': () => {
        this.metrics.event('Auth.userToken.fail', 'Access token expired, logged out', {level: Level.WARN});
        this.refreshNoToken();
      },
      'auth.apiFailure': () => {
        this.metrics.event('Auth.apiFailure', 'API for getting user profile failed. Showing message to user.', {level: Level.WARN});
        this.setState({error: ErrorTypes.UNKNOWN});
      },
      'auth.initializeError': () => {
        // If there is an error during initialization, adobeIMS does an internal redirect
        // to the login page therefore we don't need to handle it ourselves.
        this.metrics.event('Auth.initializeError', 'IMS Token check call aborted. Showing login page to user.', {level: Level.ERROR});
      },
      'auth.rateLimited': () => {
        this.metrics.event('Auth.rateLimited', 'IMS is rate limited. Showing message to user.', {level: Level.WARN});
        this.setState({error: ErrorTypes.RATE_LIMIT});
      },
      'auth.validatetokenfailed': () => {
        this.metrics.event('Auth.userToken.fail', 'User token validation failed, logged out', {level: Level.WARN});
        this.refreshNoToken();
      }
    };

    if (navigator.onLine === false) {
      window.addEventListener('online', () => location.reload());
      // User is offline. This can happen with a service worker.
      this.setState({error: ErrorTypes.OFFLINE});
    }

    this.metrics.event('Auth.start', getCurrentTenantAndPath());
    const auth = await AuthInstance({
      env: this.config.environment,
      imsConfig: Object.assign({}, this.config.ims),
      listeners
    });

    let customToken = getQueryValue('customtoken');
    // Only allow use of custom tokens on QA and while E2E testing with Cypress.
    // There is a security vulnerability by allowing tokens in query params,
    // whereby tokens can end up in logs and potentially be leaked to 3rd parties.
    if (customToken) {
      if (['dev', 'qa'].includes(this.config.environment)) {
        await auth.setCustomToken(customToken);
      } else {
        customToken = false;
      }
    }

    this.shellTimer.time('imslib.loaded');

    let ssoDomain;
    const {hash: initialHash} = this.authHistory.location;
    if (initialHash?.startsWith(SSO_PREFIX)) {
      const [, domain, hash = ''] = initialHash.match(SSO_REGEX) || [];
      if (domain && domain.startsWith('@') && domain.includes('.')) {
        ssoDomain = domain;
      }
      window.location.hash = '#' + hash;
    }

    let isLogin = false;
    if (auth.isAuthenticated(!!customToken)) {
      // If the user is already authenticated and the path is login, redirect to home
      if (this.authHistory.location.pathname === '/login') {
        this.authHistory.push('/#/home');
      }

      const redirectFromIms = auth.fromIms();
      const localSessionId = await unifiedShellSession.get('session');
      const authSessionId = auth.getSessionId();

      if (redirectFromIms) {
        // This is the trigger that can be used to
        // replace the history with the hash of the login history. The history types
        // are different because login uses browser history while the app uses hash
        // history.
        let {hash} = window.location;
        const params = getQueryParams(hashToPath({hash, removePrecedingSlash: true}));
        if (params.old_hash) {
          hash = decodeURIComponent(params.old_hash);
          // Remove any extra old_hash's leaving only the rightmost (oldest) value
          hash = hash.replace(/.*old_hash=#/, '');
          this.metrics.info('Login.old_hash', {hash});
        }
        history.replace(hashToPath({hash}));
        localSessionId === authSessionId && this.metrics.event('Auth.alreadyLoggedIn');
      }
      // If the user is authenticated and sessionId is empty or out of date,
      // treat it as login to trigger SSO handler in Core.
      if (localSessionId !== authSessionId) {
        // If the session is stale, clear it.
        localSessionId && unifiedShellSession.set({});
        redirectFromIms || this.metrics.event(
          'Auth.SSO', {authSessionId: !!authSessionId, referrer: document.referrer, storageSessionId: !!localSessionId}
        );
        isLogin = true;
      }
      // User is authenticated and loading is complete.
      this.preloader.onNeed('auth');

      return this.setState({auth, isLogin, loading: false});
    }

    // If this is a whole new Unified Shell version, let's download it while the user is in SUSI.
    const serviceWorkerService = new ServiceWorkerControllerService();
    serviceWorkerService.prepareForLogin();

    this.metrics.event('Login.imsSSO.ready', 'Invalid session: imsSSO login required');

    auth.signIn(ssoDomain);
  }

  refreshNoToken() {
    this.setState({reloading: true}, () => this.authHistory.go());
  }

  /**
   * Move Shell back to a spinner ahead of a logout or reload.
   * Done in order to remove the active iframe and prevent navigation block.
   */
  showSpinner() {
    return new Promise(resolve => this.setState({reloading: true}, resolve));
  }

  render() {
    const {defaultComponent, firstRender, loading, reloading} = this.state;
    // If loading or logging off, show spinner.
    const renderSpinner = reloading || loading;
    // In order for React to hydrate, inital render must match the HTML DOM
    // (Which does not contain Core).
    const renderCore = !(firstRender || reloading);

    // If already loaded, track page.
    loading || this.metrics.analytics.trackPage();

    return (
      <I18nProvider autoLocale localeData={coreLocaleData}>
        <I18nConsumer>
          {context => {
            return (
              <React.Fragment>
                {(renderSpinner || !context.intlReady) ? <Provider>{defaultComponent}</Provider> : null}
                {renderCore ? <Core
                  auth={this.state.auth}
                  config={this.config}
                  defaultComponent={defaultComponent}
                  error={this.state.error}
                  htmlReadyPromise={this.props.htmlReadyPromise}
                  isLogin={this.state.isLogin}
                  listedApps={this.props.listedApps}
                  metricsEnv={this.props.metricsEnv}
                  shellTimer={this.shellTimer}
                  showSpinner={() => this.showSpinner()}>
                  {this.props.children}
                </Core> : null}
              </React.Fragment>
            );
          }}
        </I18nConsumer>
      </I18nProvider>
    );
  }
}
